// src/pages/admin/PharmacyManagement.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Info, AlertCircle, X, MapPin, Phone, Mail, Clock, Package, Search, Star, TrendingUp, Eye, Trash2, CheckCircle, XCircle } from 'lucide-react';
import usePermissions from '../../hooks/usePermissions';

const API_URL = 'https://medigo-api.onrender.com/api';

// Pharmacy Detail Modal Component
const PharmacyDetailModal = ({ pharmacy, onClose }) => {
  if (!pharmacy) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Pharmacy Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-2xl"
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Package className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900">{pharmacy.name}</h3>
                <p className="text-gray-600 mt-1">{pharmacy.user?.name || 'Owner'}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    pharmacy.verificationStatus === 'verified' ? 'bg-green-100 text-green-800' :
                    pharmacy.verificationStatus === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {pharmacy.verificationStatus || 'pending'}
                  </span>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    pharmacy.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {pharmacy.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-purple-600" />
              Contact Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="text-gray-900 font-medium">{pharmacy.phone || pharmacy.contact?.phone || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="text-gray-900 font-medium">{pharmacy.email || pharmacy.contact?.email || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 col-span-2">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="text-gray-900 font-medium">
                    {pharmacy.location?.address || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-600">City</p>
                  <p className="text-gray-900 font-medium">
                    {pharmacy.location?.city || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* License Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              License Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">License Number</p>
                <p className="text-lg font-semibold text-gray-900">
                  {pharmacy.licenseNumber || 'Not Provided'}
                </p>
              </div>
              {pharmacy.licenseExpiry && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">License Expiry</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(pharmacy.licenseExpiry).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Subscription Info */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Plan</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">
                {pharmacy.subscription?.plan || 'free'}
              </p>
            </div>
          </div>

          {/* Description */}
          {pharmacy.description && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">About</h4>
              <p className="text-gray-700 leading-relaxed">{pharmacy.description}</p>
            </div>
          )}

          {/* Statistics */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center bg-purple-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-purple-600">{pharmacy.orderCount || pharmacy.stats?.totalOrders || 0}</p>
                <p className="text-sm text-gray-600 mt-1">Total Orders</p>
              </div>
              <div className="text-center bg-blue-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-blue-600">{pharmacy.productCount || pharmacy.stats?.totalProducts || 0}</p>
                <p className="text-sm text-gray-600 mt-1">Products</p>
              </div>
              <div className="text-center bg-green-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-green-600">
                  {pharmacy.rating?.average ? pharmacy.rating.average.toFixed(1) : 'N/A'}
                </p>
                <p className="text-sm text-gray-600 mt-1">Rating</p>
              </div>
              <div className="text-center bg-orange-50 rounded-lg p-4">
                <p className="text-2xl font-bold text-orange-600">{pharmacy.rating?.totalReviews || 0}</p>
                <p className="text-sm text-gray-600 mt-1">Reviews</p>
              </div>
            </div>
          </div>

          {/* Operating Hours */}
          {pharmacy.operatingHours && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" />
                Operating Hours
              </h4>
              {pharmacy.operatingHours.is24Hours ? (
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-green-800 font-semibold text-lg">Open 24/7</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(pharmacy.operatingHours).map(([day, hours]) => {
                    if (day === 'is24Hours' || !hours) return null;
                    return (
                      <div key={day} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                        <span className="font-medium text-gray-900 capitalize">{day}</span>
                        <span className="text-gray-600">
                          {hours.isClosed ? 'Closed' : `${hours.open || 'N/A'} - ${hours.close || 'N/A'}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Verification Notes */}
          {pharmacy.verificationNotes && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Verification Notes</h4>
              <p className="text-gray-900">{pharmacy.verificationNotes}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <p>Created: {new Date(pharmacy.createdAt).toLocaleString()}</p>
              <p>Last Updated: {new Date(pharmacy.updatedAt).toLocaleString()}</p>
              {pharmacy.verifiedAt && (
                <p>Verified On: {new Date(pharmacy.verifiedAt).toLocaleString()}</p>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Reject Modal Component
const RejectModal = ({ pharmacy, onClose, onReject, loading }) => {
  const [rejectReason, setRejectReason] = useState('');

  const handleSubmit = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Reject Pharmacy</h2>
        </div>
        
        <div className="p-6 space-y-4">
          <p className="text-gray-600">
            You are about to reject <strong>{pharmacy.name}</strong>. 
            Please provide a reason for rejection.
          </p>
          
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reason for rejection..."
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            required
          />

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!rejectReason.trim() || loading}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Rejecting...' : 'Reject Pharmacy'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PharmacyManagement = () => {
  const {
    canViewPharmacies,
    canApprovePharmacies,
    canDeletePharmacies,
    canTogglePharmacyStatus,
    isSuperAdmin,
    isAdmin
  } = usePermissions();

  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    verificationStatus: '',
    isVerified: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    page: 1
  });
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (canViewPharmacies) {
      fetchPharmacies();
    }
  }, [filters, canViewPharmacies]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('medigo_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      }
    };
  };

  const fetchPharmacies = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });
      params.append('_t', Date.now());

      const response = await axios.get(`${API_URL}/admin/pharmacies?${params}`, getAuthHeaders());

      if (response.data.success) {
        const data = Array.isArray(response.data.data) ? response.data.data : [];
        setPharmacies(data);
        
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        } else {
          setPagination({
            total: data.length,
            pages: Math.ceil(data.length / filters.limit),
            page: filters.page
          });
        }
      }
      setError('');
    } catch (err) {
      console.error('Error fetching pharmacies:', err);
      setError(err.response?.data?.message || 'Failed to load pharmacies');
    } finally {
      setLoading(false);
    }
  };

  const fetchPharmacyDetails = async (pharmacyId) => {
    try {
      setLoadingDetail(true);
      const response = await axios.get(`${API_URL}/admin/pharmacies/${pharmacyId}`, getAuthHeaders());
      
      setSelectedPharmacy(response.data.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error fetching pharmacy details:', err);
      setError(err.response?.data?.message || 'Failed to load pharmacy details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleApprove = async (pharmacyId) => {
    if (!canApprovePharmacies) {
      setError('You do not have permission to approve pharmacies');
      return;
    }

    if (!window.confirm('Are you sure you want to approve this pharmacy?')) return;
    
    try {
      setActionLoading(true);
      const response = await axios.patch(
        `${API_URL}/admin/pharmacies/${pharmacyId}/verify`,
        {},
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess('Pharmacy approved successfully');
        await fetchPharmacies();
      }
    } catch (err) {
      console.error('Error approving pharmacy:', err);
      setError(err.response?.data?.message || 'Failed to approve pharmacy');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (reason) => {
    if (!canApprovePharmacies || !selectedPharmacy) {
      setError('You do not have permission to reject pharmacies');
      return;
    }
    
    try {
      setActionLoading(true);
      const response = await axios.patch(
        `${API_URL}/admin/pharmacies/${selectedPharmacy._id}/reject`,
        { reason },
        getAuthHeaders()
      );
      
      if (response.data.success) {
        setSuccess('Pharmacy rejected');
        setShowRejectModal(false);
        setSelectedPharmacy(null);
        await fetchPharmacies();
      }
    } catch (err) {
      console.error('Error rejecting pharmacy:', err);
      setError(err.response?.data?.message || 'Failed to reject pharmacy');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async (pharmacyId, currentStatus) => {
    if (!canTogglePharmacyStatus) {
      setError('You do not have permission to change pharmacy status');
      return;
    }

    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this pharmacy?`)) return;
    
    try {
      setActionLoading(true);
      const response = await axios.patch(
        `${API_URL}/admin/pharmacies/${pharmacyId}/status`,
        {},
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess(response.data.message || 'Pharmacy status updated successfully');
        await fetchPharmacies();
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError(err.response?.data?.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (pharmacyId) => {
    if (!canDeletePharmacies) {
      setError('Only Super Admins can delete pharmacies');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this pharmacy? This action cannot be undone.')) return;
    
    try {
      setActionLoading(true);
      const response = await axios.delete(
        `${API_URL}/admin/pharmacies/${pharmacyId}`,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess('Pharmacy deleted successfully');
        await fetchPharmacies();
      }
    } catch (err) {
      console.error('Error deleting pharmacy:', err);
      setError(err.response?.data?.message || 'Failed to delete pharmacy');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      verified: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return badges[status] || badges.pending;
  };

  if (!canViewPharmacies) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-8 h-8 text-purple-600" />
            Pharmacy Management
            {isSuperAdmin && <Shield className="w-6 h-6 text-purple-600" />}
          </h1>
          <p className="text-gray-600 mt-1">Manage and verify pharmacies</p>
        </div>
      </div>

      {/* Permission Badge */}
      {isSuperAdmin ? (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-3">
          <Shield className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <p className="text-purple-900 font-medium">Super Admin Privileges Active</p>
            <p className="text-purple-700 text-sm">
              You can approve, verify, and delete pharmacies
            </p>
          </div>
        </div>
      ) : isAdmin ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-blue-900 font-medium">Admin Access</p>
            <p className="text-blue-700 text-sm">
              You can approve and verify pharmacies, but cannot delete them
            </p>
          </div>
        </div>
      ) : null}

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-800">{error}</p>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800 text-xl">×</button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-800">{success}</p>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800 text-xl">×</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Pharmacies</p>
              <p className="text-2xl font-bold text-gray-900">{pagination.total || 0}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Verified</p>
              <p className="text-2xl font-bold text-green-600">
                {pharmacies.filter(p => p.isVerified || p.verificationStatus === 'verified').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {pharmacies.filter(p => !p.isVerified && p.verificationStatus === 'pending').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-purple-600">
                {pharmacies.filter(p => p.isActive).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, phone, or location..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <select
            value={filters.verificationStatus}
            onChange={(e) => setFilters({ ...filters, verificationStatus: e.target.value, page: 1 })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
          </select>

          <select
            value={filters.isVerified}
            onChange={(e) => setFilters({ ...filters, isVerified: e.target.value, page: 1 })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="">All Verification</option>
            <option value="true">Verified</option>
            <option value="false">Not Verified</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : pharmacies.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No pharmacies found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pharmacy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">License</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pharmacies.map((pharmacy) => (
                  <tr key={pharmacy._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{pharmacy.name}</p>
                        <p className="text-sm text-gray-600">{pharmacy.user?.name || pharmacy.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="text-gray-900">{pharmacy.phone || pharmacy.contact?.phone || 'N/A'}</p>
                        <p className="text-gray-600">{pharmacy.email || pharmacy.contact?.email || 'N/A'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <MapPin className="w-4 h-4" />
                        {pharmacy.location?.city || pharmacy.location?.address || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{pharmacy.licenseNumber || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(pharmacy.verificationStatus || (pharmacy.isVerified ? 'verified' : 'pending'))}`}>
                          {pharmacy.verificationStatus || (pharmacy.isVerified ? 'verified' : 'pending')}
                        </span>
                        {pharmacy.isActive && (
                          <span className="block text-xs text-green-600">● Active</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span className="text-sm text-gray-900">
                          {pharmacy.rating?.average ? pharmacy.rating.average.toFixed(1) : '0.0'}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({pharmacy.rating?.totalReviews || 0})
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => fetchPharmacyDetails(pharmacy._id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                          disabled={actionLoading}
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {canApprovePharmacies && (pharmacy.verificationStatus === 'pending' || (!pharmacy.isVerified && !pharmacy.verificationStatus)) && (
                          <>
                            <button
                              onClick={() => handleApprove(pharmacy._id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Approve"
                              disabled={actionLoading}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedPharmacy(pharmacy);
                                setShowRejectModal(true);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Reject"
                              disabled={actionLoading}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}

                        {canTogglePharmacyStatus && (
                          <button
                            onClick={() => handleToggleStatus(pharmacy._id, pharmacy.isActive)}
                            className={`p-2 rounded-lg transition-colors ${
                              pharmacy.isActive 
                                ? 'text-orange-600 hover:bg-orange-50' 
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={pharmacy.isActive ? 'Deactivate' : 'Activate'}
                            disabled={actionLoading}
                          >
                            <AlertCircle className="w-4 h-4" />
                          </button>
                        )}

                        {canDeletePharmacies ? (
                          <button
                            onClick={() => handleDelete(pharmacy._id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete (Super Admin Only)"
                            disabled={actionLoading}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <span
                            className="p-2 text-gray-400 cursor-not-allowed"
                            title="Only Super Admins can delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between bg-white px-6 py-3 rounded-lg shadow">
          <div className="text-sm text-gray-700">
            Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
              disabled={filters.page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              disabled={filters.page === pagination.pages}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Pharmacy Detail Modal */}
      {showDetailModal && selectedPharmacy && (
        <PharmacyDetailModal
          pharmacy={selectedPharmacy}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedPharmacy(null);
          }}
        />
      )}

      {/* Loading Detail Overlay */}
      {loadingDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading pharmacy details...</p>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedPharmacy && (
        <RejectModal
          pharmacy={selectedPharmacy}
          onClose={() => {
            setShowRejectModal(false);
            setSelectedPharmacy(null);
          }}
          onReject={handleReject}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default PharmacyManagement;